'use client'

import { useAtomValue } from 'jotai'
import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { DatePicker } from '@/components/widget/date-picker'
import { TIME_FRAME_OPTIONS } from '@/features/chart/lib/utils/chart-util'
import { useBalanceRows } from '@/features/dashboard/hooks/use-balance-rows'
import { ORDER_FORM_FIELD_CLASS } from '@/features/order/order-form/components/widget/order-form-fields'
import {
  portfolioMetricGap,
  type PortfolioMetricPreview,
  portfolioMetricPreview,
} from '@/features/order/order-form/lib/portfolio-metric-preview'
import type {
  ActionEventNode,
  CandleNode,
  DbNewsNode,
  IndicatorSpecValue,
  OptionIvNode,
  PortfolioMetricNode,
  PortfolioUnitToken,
  PositionStatsNode,
  PriceNode,
  PriceOffsetUnitToken,
  ReturnsNode,
  RiskMetricNode,
  ScheduleNode,
  TechnicalIndicatorNode,
} from '@/features/order/order-form/lib/trigger-condition-model'
import {
  CANDLE_FIELD_OPTIONS,
  INDICATOR_TYPE_OPTIONS,
  type Option,
  OPTION_IV_SOURCE_OPTIONS,
  PORTFOLIO_METRIC_OPTIONS,
  POSITION_METRIC_OPTIONS,
  POSITION_SCOPE_OPTIONS,
  PRICE_SOURCE_OPTIONS,
  RISK_METRIC_OPTIONS,
} from '@/features/order/order-form/lib/trigger-select-options'
import { useNumberFormat } from '@/hooks/use-number-format'
import type { NumberFormatter } from '@/lib/number-format'
import { cn } from '@/lib/utils'
import { useDbNewsVocabulary } from '@/service/db-news.hook'
import { balancesRawAtom } from '@/state/account'

import { EeTypeChipPreview } from './ee-type-chip-preview'
import {
  AccountSelect,
  AssetSelect,
  ChipInput,
  DecimalInput,
  FieldRow,
  GroupSelect,
  OperatorSelect,
  SymbolSelect,
  TextInput,
  TimeframeSelect,
  TokenSelect,
  useConditionEditorData,
} from './field-controls'

export type LeafEditorProps<N> = { node: N; onChange: (node: N) => void }

const OFFSET_UNIT_OPTIONS: Option<PriceOffsetUnitToken>[] = [
  { value: 'percent', label: '%' },
  { value: 'bps', label: 'bps' },
  { value: 'quote_value', label: 'Quote' },
  { value: 'ticks', label: 'Ticks' },
]

const WINDOW_OPTIONS: Option<string>[] = TIME_FRAME_OPTIONS.map((t) => ({
  value: String(t.value * 1000),
  label: t.label,
}))

type ConditionEditorT = ReturnType<typeof useTranslations>

function translatedOptions<T extends string>(
  options: Option<T>[],
  t: ConditionEditorT,
  namespace: string
): Option<T>[] {
  const translate = t as unknown as (key: string) => string
  return options.map((o) => ({ ...o, label: translate(`${namespace}.${o.value}`) }))
}

function Toggle<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return <SegmentedControl<T> fill options={options} value={value} onChange={onChange} />
}

function msToDate(ms?: string): Date | undefined {
  const n = Number(ms)
  if (!Number.isFinite(n) || n <= 0) return undefined
  return new Date(n)
}

function mergeDateWithStoredTime(date: Date, currentMs?: string): string {
  const current = msToDate(currentMs)
  const next = new Date(date)
  if (current) {
    next.setHours(
      current.getHours(),
      current.getMinutes(),
      current.getSeconds(),
      current.getMilliseconds()
    )
  } else {
    next.setHours(0, 0, 0, 0)
  }
  return String(next.getTime())
}

// ── Price ─────────────────────────────────────────────────────────────────────
export function PriceEditor({ node, onChange }: LeafEditorProps<PriceNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<PriceNode>) => onChange({ ...node, ...patch })
  const isRelative = !!node.relative
  return (
    <>
      <FieldRow label={t('labels.instrument')}>
        <SymbolSelect value={node.symbol} onChange={(symbol) => set({ symbol })} />
      </FieldRow>
      <FieldRow label={t('labels.source')}>
        <TokenSelect
          value={node.source}
          onChange={(source) => set({ source })}
          options={translatedOptions(PRICE_SOURCE_OPTIONS, t, 'priceSources')}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.target')}>
        <Toggle
          value={isRelative ? 'relative' : 'absolute'}
          onChange={(v) =>
            v === 'relative'
              ? set({
                  value: '',
                  relative: { anchor: node.source, offset: '', offsetUnit: 'percent' },
                })
              : set({ value: '', relative: undefined })
          }
          options={[
            { value: 'absolute', label: t('targetModes.absolute') },
            { value: 'relative', label: t('targetModes.relative') },
          ]}
        />
      </FieldRow>
      {isRelative ? (
        <>
          <FieldRow label={t('labels.anchor')}>
            <TokenSelect
              value={node.relative!.anchor}
              onChange={(anchor) => set({ relative: { ...node.relative!, anchor } })}
              options={translatedOptions(PRICE_SOURCE_OPTIONS, t, 'priceSources')}
            />
          </FieldRow>
          <FieldRow label={t('labels.offset')}>
            <div className="flex w-full gap-1.5">
              <DecimalInput
                value={node.relative!.offset}
                onChange={(offset) => set({ relative: { ...node.relative!, offset } })}
                signed
                placeholder="-5"
              />
              <TokenSelect
                value={node.relative!.offsetUnit}
                onChange={(offsetUnit) => set({ relative: { ...node.relative!, offsetUnit } })}
                options={translatedOptions(OFFSET_UNIT_OPTIONS, t, 'offsetUnits')}
                className="max-w-22"
              />
            </div>
          </FieldRow>
        </>
      ) : (
        <FieldRow label={t('labels.price')}>
          <DecimalInput
            value={node.value}
            onChange={(value) => set({ value })}
            placeholder="70000"
          />
        </FieldRow>
      )}
    </>
  )
}

// ── Technical indicator ───────────────────────────────────────────────────────
function IndicatorSpecFields({
  spec,
  onChange,
}: {
  spec: IndicatorSpecValue
  onChange: (spec: IndicatorSpecValue) => void
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<IndicatorSpecValue>) => onChange({ ...spec, ...patch })
  return (
    <div className="flex flex-col gap-2 bg-white/[0.03] p-2">
      <FieldRow label={t('labels.type')}>
        <TokenSelect
          value={spec.indicatorType}
          onChange={(indicatorType) => set({ indicatorType })}
          options={translatedOptions(INDICATOR_TYPE_OPTIONS, t, 'indicatorTypes')}
        />
      </FieldRow>
      <FieldRow label={t('labels.instrument')}>
        <SymbolSelect value={spec.symbol} onChange={(symbol) => set({ symbol })} />
      </FieldRow>
      <FieldRow label={t('labels.source')}>
        <TokenSelect
          value={spec.source}
          onChange={(source) => set({ source })}
          options={translatedOptions(PRICE_SOURCE_OPTIONS, t, 'priceSources')}
        />
      </FieldRow>
      <FieldRow label={t('labels.period')}>
        <DecimalInput
          value={spec.periodCount}
          onChange={(periodCount) => set({ periodCount })}
          placeholder="10"
        />
      </FieldRow>
      <FieldRow label={t('labels.timeframe')}>
        <TimeframeSelect
          value={spec.timeframeSeconds}
          onChange={(timeframeSeconds) => set({ timeframeSeconds })}
        />
      </FieldRow>
    </div>
  )
}

export function TechnicalIndicatorEditor({
  node,
  onChange,
}: LeafEditorProps<TechnicalIndicatorNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<TechnicalIndicatorNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <p className="text-[10px] tracking-tight text-white/30 uppercase">{t('indicator')}</p>
      <IndicatorSpecFields spec={node.left} onChange={(left) => set({ left })} />
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.compareTo')}>
        <Toggle
          value={node.targetMode}
          onChange={(targetMode) => set({ targetMode })}
          options={[
            { value: 'indicator', label: t('compareModes.indicator') },
            { value: 'threshold', label: t('compareModes.value') },
          ]}
        />
      </FieldRow>
      {node.targetMode === 'indicator' ? (
        <IndicatorSpecFields spec={node.right} onChange={(right) => set({ right })} />
      ) : (
        <FieldRow label={t('labels.threshold')}>
          <DecimalInput
            value={node.threshold}
            onChange={(threshold) => set({ threshold })}
            signed
            placeholder="65000"
          />
        </FieldRow>
      )}
    </>
  )
}

// ── Returns ─────────────────────────────────────────────────────────────────--
export function ReturnsEditor({ node, onChange }: LeafEditorProps<ReturnsNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<ReturnsNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.instrument')}>
        <SymbolSelect value={node.symbol} onChange={(symbol) => set({ symbol })} />
      </FieldRow>
      <FieldRow label={t('labels.source')}>
        <TokenSelect
          value={node.source}
          onChange={(source) => set({ source })}
          options={translatedOptions(PRICE_SOURCE_OPTIONS, t, 'priceSources')}
        />
      </FieldRow>
      <FieldRow label={t('labels.overWindow')}>
        <TokenSelect
          value={node.windowMs}
          onChange={(windowMs) => set({ windowMs })}
          options={WINDOW_OPTIONS}
          placeholder={t('labels.window')}
        />
      </FieldRow>
      <FieldRow label={t('labels.sampling')}>
        <TimeframeSelect
          value={node.timeframeSeconds}
          onChange={(timeframeSeconds) => set({ timeframeSeconds })}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.return')} help={t('help.return')}>
        <DecimalInput
          value={node.valuePercent}
          onChange={(valuePercent) => set({ valuePercent })}
          signed
          suffix="%"
          placeholder="-4"
        />
      </FieldRow>
    </>
  )
}

// ── Candle ─────────────────────────────────────────────────────────────────---
export function CandleEditor({ node, onChange }: LeafEditorProps<CandleNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<CandleNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.instrument')}>
        <SymbolSelect value={node.symbol} onChange={(symbol) => set({ symbol })} />
      </FieldRow>
      <FieldRow label={t('labels.timeframe')}>
        <TimeframeSelect
          value={node.timeframeSeconds}
          onChange={(timeframeSeconds) => set({ timeframeSeconds })}
        />
      </FieldRow>
      <FieldRow label={t('labels.field')}>
        <TokenSelect
          value={node.field}
          onChange={(field) => set({ field })}
          options={translatedOptions(CANDLE_FIELD_OPTIONS, t, 'candleFields')}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.value')}>
        <DecimalInput value={node.value} onChange={(value) => set({ value })} placeholder="70000" />
      </FieldRow>
      <FieldRow label={t('labels.onOpenCandle')} help={t('help.onOpenCandle')}>
        <Toggle
          value={node.allowOpenCandle ? 'yes' : 'no'}
          onChange={(v) => set({ allowOpenCandle: v === 'yes' })}
          options={[
            { value: 'no', label: t('openCandleModes.closedOnly') },
            { value: 'yes', label: t('openCandleModes.allowOpen') },
          ]}
        />
      </FieldRow>
    </>
  )
}

// ── Portfolio metric ───────────────────────────────────────────────────────---
export function PortfolioMetricEditor({ node, onChange }: LeafEditorProps<PortfolioMetricNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const { defaultAccountId } = useConditionEditorData()
  const set = (patch: Partial<PortfolioMetricNode>) => onChange({ ...node, ...patch })
  const accountId = node.accountId || defaultAccountId
  const accountIds = useMemo(() => (accountId ? [accountId] : []), [accountId])
  const balances = useBalanceRows(accountIds)
  const rawBalances = useAtomValue(balancesRawAtom)
  const rawRows = useMemo(
    () => rawBalances.filter((r) => r.accountId === accountId),
    [rawBalances, accountId]
  )
  const currencyOptions = useMemo<Option<string>[]>(() => {
    const out = [...new Set(balances.map((b) => b.currency.toUpperCase()))].map((c) => ({
      value: c,
      label: c,
    }))
    if (node.currency && !out.some((o) => o.value === node.currency)) {
      out.unshift({ value: node.currency, label: node.currency })
    }
    return out
  }, [balances, node.currency])

  const isBalance = node.metric === 'currency_balance'
  const currency = node.currency.trim().toUpperCase()
  const valueSuffix = !isBalance || node.unit === 'usd' ? 'USD' : currency || undefined
  const preview = portfolioMetricPreview(node, balances, rawRows)
  const gap = portfolioMetricGap(node, balances, rawRows)
  const fmt = useNumberFormat()
  const unitOptions: Option<PortfolioUnitToken>[] = [
    { value: 'native', label: currency || t('portfolioUnits.native') },
    { value: 'usd', label: 'USD' },
  ]

  return (
    <>
      <FieldRow label={t('labels.account')}>
        <AccountSelect
          value={node.accountId}
          onChange={(accountId) => set({ accountId, currency: '' })}
        />
      </FieldRow>
      <FieldRow label={t('labels.metric')}>
        <TokenSelect
          value={node.metric}
          onChange={(metric) => set({ metric })}
          options={translatedOptions(PORTFOLIO_METRIC_OPTIONS, t, 'portfolioMetrics')}
        />
      </FieldRow>
      {isBalance && (
        <>
          <FieldRow label={t('labels.currency')}>
            <TokenSelect
              value={node.currency}
              onChange={(currency) => set({ currency })}
              options={currencyOptions}
              placeholder={t('labels.currency')}
            />
          </FieldRow>
          <FieldRow label={t('labels.unit')} help={t('help.portfolioUnit')}>
            <Toggle value={node.unit} onChange={(unit) => set({ unit })} options={unitOptions} />
          </FieldRow>
        </>
      )}
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.value')}>
        <DecimalInput
          value={node.value}
          onChange={(value) => set({ value })}
          signed
          suffix={valueSuffix}
          placeholder={isBalance && node.unit === 'native' ? '10' : '100000'}
        />
        {gap ? (
          <p className="text-ox-gold mt-1 text-xs">
            {gap.kind === 'account'
              ? t('portfolioUnavailable.account')
              : t('portfolioUnavailable.currencyUsd', { currency: gap.currency })}
          </p>
        ) : (
          preview && (
            <p className="text-muted-foreground mt-1 text-xs tabular-nums">
              {t('portfolioCurrent', { value: formatPreview(fmt, preview, isBalance) })}
            </p>
          )
        )}
      </FieldRow>
    </>
  )
}

function formatPreview(
  fmt: NumberFormatter,
  preview: PortfolioMetricPreview,
  isBalance: boolean
): string {
  const usd = preview.usd === undefined ? undefined : fmt.usd(preview.usd, { sensitive: true })
  if (!isBalance || preview.native === undefined) return usd ?? '—'
  const native = `${fmt.number(preview.native, { maximumFractionDigits: 8, sensitive: true })} ${preview.currency}`
  return usd ? `${native} (≈ ${usd})` : native
}

// ── Position stats ─────────────────────────────────────────────────────────---
export function PositionStatsEditor({ node, onChange }: LeafEditorProps<PositionStatsNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<PositionStatsNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.scope')}>
        <TokenSelect
          value={node.scopeKind}
          onChange={(scopeKind) => set({ scopeKind, scopeValue: '' })}
          options={translatedOptions(POSITION_SCOPE_OPTIONS, t, 'positionScopes')}
        />
      </FieldRow>
      {node.scopeKind === 'symbol' && (
        <FieldRow label={t('labels.symbol')}>
          <SymbolSelect value={node.scopeValue} onChange={(scopeValue) => set({ scopeValue })} />
        </FieldRow>
      )}
      {node.scopeKind === 'underlying' && (
        <FieldRow label={t('labels.underlying')}>
          <AssetSelect value={node.scopeValue} onChange={(scopeValue) => set({ scopeValue })} />
        </FieldRow>
      )}
      {node.scopeKind === 'group' && (
        <FieldRow label={t('labels.group')}>
          <GroupSelect value={node.scopeValue} onChange={(scopeValue) => set({ scopeValue })} />
        </FieldRow>
      )}
      <FieldRow label={t('labels.metric')}>
        <TokenSelect
          value={node.metric}
          onChange={(metric) => set({ metric })}
          options={translatedOptions(POSITION_METRIC_OPTIONS, t, 'positionMetrics')}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.value')}>
        <DecimalInput
          value={node.value}
          onChange={(value) => set({ value })}
          signed
          placeholder="-5000"
        />
      </FieldRow>
    </>
  )
}

// ── Risk metric ────────────────────────────────────────────────────────────---
export function RiskMetricEditor({ node, onChange }: LeafEditorProps<RiskMetricNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<RiskMetricNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.asset')}>
        <AssetSelect value={node.asset} onChange={(asset) => set({ asset })} />
      </FieldRow>
      <FieldRow label={t('labels.metric')}>
        <TokenSelect
          value={node.metric}
          onChange={(metric) => set({ metric })}
          options={translatedOptions(RISK_METRIC_OPTIONS, t, 'riskMetrics')}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.value')}>
        <DecimalInput
          value={node.value}
          onChange={(value) => set({ value })}
          signed
          placeholder="10"
        />
      </FieldRow>
    </>
  )
}

// ── Option IV ──────────────────────────────────────────────────────────────---
export function OptionIvEditor({ node, onChange }: LeafEditorProps<OptionIvNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<OptionIvNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.option')}>
        <SymbolSelect
          value={node.symbol}
          onChange={(symbol) => set({ symbol })}
          placeholder="BTC-…-C"
        />
      </FieldRow>
      <FieldRow label={t('labels.underlying')}>
        <AssetSelect value={node.underlying} onChange={(underlying) => set({ underlying })} />
      </FieldRow>
      <FieldRow label={t('labels.source')}>
        <TokenSelect
          value={node.source}
          onChange={(source) => set({ source })}
          options={translatedOptions(OPTION_IV_SOURCE_OPTIONS, t, 'optionIvSources')}
        />
      </FieldRow>
      <FieldRow label={t('labels.operator')}>
        <OperatorSelect value={node.op} onChange={(op) => set({ op })} />
      </FieldRow>
      <FieldRow label={t('labels.iv')} help={t('help.iv')}>
        <DecimalInput
          value={node.valuePercent}
          onChange={(valuePercent) => set({ valuePercent })}
          suffix="%"
          placeholder="65"
        />
      </FieldRow>
    </>
  )
}

// ── Schedule ───────────────────────────────────────────────────────────────---
export function ScheduleEditor({ node, onChange }: LeafEditorProps<ScheduleNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<ScheduleNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.when')}>
        <Toggle
          value={node.variant}
          onChange={(variant) => set({ variant, value: '' })}
          options={[
            { value: 'delay', label: t('scheduleModes.delay') },
            { value: 'fire_at', label: t('scheduleModes.fireAt') },
          ]}
        />
      </FieldRow>
      {node.variant === 'delay' ? (
        <FieldRow label={t('labels.delay')}>
          <DecimalInput
            value={node.value}
            onChange={(value) => set({ value })}
            suffix="sec"
            placeholder="3600"
          />
        </FieldRow>
      ) : (
        <FieldRow label={t('labels.fireAt')}>
          <DatePicker
            withTime
            selected={msToDate(node.value)}
            onSelect={(date) => set({ value: String(date.getTime()) })}
            minDate={new Date()}
            className={cn(ORDER_FORM_FIELD_CLASS, 'min-w-0 flex-1 shadow-none')}
          />
        </FieldRow>
      )}
    </>
  )
}

// ── DB News ────────────────────────────────────────────────────────────────---
export function DbNewsEditor({ node, onChange }: LeafEditorProps<DbNewsNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<DbNewsNode>) => onChange({ ...node, ...patch })
  // Suggestions only — the chip inputs stay free text, so a value not (yet) in the
  // live vocabulary can still be entered. Fetched once the News editor is shown and
  // cached for 5 minutes (the backend's refresh cadence).
  const { data: vocab } = useDbNewsVocabulary()
  return (
    <>
      <p className="text-[10px] leading-tight text-white/40">{t('dbNewsDescription')}</p>
      <FieldRow label={t('labels.eeType')}>
        <ChipInput
          values={node.eeType}
          onChange={(eeType) => set({ eeType })}
          placeholder="ETF_APPROVAL"
          suggestions={vocab?.eeTypes}
          renderChipPreview={(chip, content) => (
            <EeTypeChipPreview eeType={chip}>{content}</EeTypeChipPreview>
          )}
        />
      </FieldRow>
      <FieldRow label={t('labels.newsType')}>
        <ChipInput
          values={node.newsType}
          onChange={(newsType) => set({ newsType })}
          placeholder={t('placeholders.type')}
          suggestions={vocab?.newsTypes}
        />
      </FieldRow>
      <FieldRow label={t('labels.coins')}>
        <ChipInput
          values={node.coins}
          onChange={(coins) => set({ coins })}
          placeholder="BTC"
          suggestions={vocab?.coins}
        />
      </FieldRow>
      <FieldRow label={t('labels.tags')}>
        <ChipInput
          values={node.tags}
          onChange={(tags) => set({ tags })}
          placeholder={t('placeholders.tag')}
          suggestions={vocab?.tags}
        />
      </FieldRow>
      <FieldRow label={t('labels.textContains')} help={t('help.textContains')}>
        <ChipInput
          values={node.textContains}
          onChange={(textContains) => set({ textContains })}
          placeholder={t('placeholders.approved')}
        />
      </FieldRow>
      <FieldRow label={t('labels.onlyAfter')} help={t('help.onlyAfter')}>
        <div className="flex gap-1.5">
          <DatePicker
            selected={msToDate(node.eventNotBeforeMs)}
            onSelect={(date) =>
              set({ eventNotBeforeMs: mergeDateWithStoredTime(date, node.eventNotBeforeMs) })
            }
            className={cn(ORDER_FORM_FIELD_CLASS, 'min-w-0 flex-1 shadow-none')}
          />
          {node.eventNotBeforeMs && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => set({ eventNotBeforeMs: undefined })}
              className={cn(
                ORDER_FORM_FIELD_CLASS,
                'text-foreground/40 hover:text-foreground/70 flex w-9 shrink-0 items-center justify-center px-0'
              )}
              aria-label={t('clear')}
            >
              <XIcon className="size-3.5" />
            </Button>
          )}
        </div>
      </FieldRow>
    </>
  )
}

// ── Action event (legacy / migrated only — not offered in the kind picker) ─────
const ACTION_EVENT_OPTIONS: Option<ActionEventNode['eventType']>[] = [
  { value: 'placed', label: 'placed' },
  { value: 'filled', label: 'filled' },
  { value: 'partially_filled', label: 'partially_filled' },
  { value: 'cancelled', label: 'cancelled' },
  { value: 'failed', label: 'failed' },
]
const ACTION_TARGET_OPTIONS: Option<ActionEventNode['targetType']>[] = [
  { value: 'order_id', label: 'order_id' },
  { value: 'algo_id', label: 'algo_id' },
  { value: 'symbol', label: 'symbol' },
]

export function ActionEventEditor({ node, onChange }: LeafEditorProps<ActionEventNode>) {
  const t = useTranslations('orderForm.conditionEditor')
  const set = (patch: Partial<ActionEventNode>) => onChange({ ...node, ...patch })
  return (
    <>
      <FieldRow label={t('labels.event')}>
        <TokenSelect
          value={node.eventType}
          onChange={(eventType) => set({ eventType })}
          options={translatedOptions(ACTION_EVENT_OPTIONS, t, 'actionEvents')}
        />
      </FieldRow>
      <FieldRow label={t('labels.target')}>
        <TokenSelect
          value={node.targetType}
          onChange={(targetType) => set({ targetType })}
          options={translatedOptions(ACTION_TARGET_OPTIONS, t, 'actionTargets')}
        />
      </FieldRow>
      <FieldRow label={t('labels.targetValue')}>
        <TextInput
          value={node.targetValue}
          onChange={(targetValue) => set({ targetValue })}
          placeholder={t('placeholders.idOrSymbol')}
        />
      </FieldRow>
      {node.eventType === 'partially_filled' && (
        <FieldRow label={t('labels.fillPercent')}>
          <DecimalInput
            value={node.partialFillThreshold}
            onChange={(partialFillThreshold) => set({ partialFillThreshold })}
            suffix="%"
            placeholder="50"
          />
        </FieldRow>
      )}
    </>
  )
}
